import type { ParsedStatement, ParsedTransaction } from "@paisa/types"
import { parseHdfcNarration } from "./narration"

// ── Column x-boundaries ──────────────────────────────────────────────────
// Derived from HDFC PDF coordinate analysis. Numbers are right-aligned so
// their x (left edge) varies — these are the column boundaries, not header positions.
const X_DATE_MAX = 56 // Date: x ≤ 56
const X_NARRATION_MIN = 60 // Narration: 60 ≤ x < 280
const X_NARRATION_MAX = 280
const X_REF_MIN = 280 // Ref No: 280 ≤ x < 355
const X_VALUEDT_MIN = 355 // Value Dt: 355 ≤ x < 402
const X_WITHDRAWAL_MIN = 402 // Withdrawal: 402 ≤ x < 490
const X_WITHDRAWAL_MAX = 490
const X_DEPOSIT_MIN = 490 // Deposit: 490 ≤ x < 560
const X_DEPOSIT_MAX = 560
const X_BALANCE_MIN = 560 // Balance: x ≥ 560

// ── Y-coordinate boundaries ───────────────────────────────────────────────
const Y_DATA_MAX = 618 // y ≥ 619 = per-page header block, skip
const Y_DATA_MIN = 55 // y ≤ 55 = footer block, skip
const ROW_TOLERANCE = 4 // items within ±4 y-units are on the same logical row

// ── Detect ───────────────────────────────────────────────────────────────

/**
 * Detect whether raw text extracted from a PDF looks like an HDFC savings statement.
 */
export function isHdfcSavingsPdf(rawContent: string): boolean {
  return (
    /HDFC BANK/i.test(rawContent) &&
    /Statement From/i.test(rawContent) &&
    /Narration/i.test(rawContent)
  )
}

// ── Parse ────────────────────────────────────────────────────────────────

/**
 * Parse an HDFC savings/current account PDF statement.
 *
 * @param data      Raw PDF file as ArrayBuffer
 * @param password  Optional PDF password (HDFC uses the customer ID: the numeric ID)
 */
export function parseHdfcSavingsStatementItems(items: Coord[]): ParsedStatement {
  const metadata = extractMetadata(items)
  const transactions = parseHdfcSavingsItems(items)

  return {
    bank: "hdfc",
    accountType: metadata.accountType,
    accountNumber: metadata.accountLast4,
    periodStart: metadata.periodStart,
    periodEnd: metadata.periodEnd,
    transactions,
  }
}

// ── Text extraction ───────────────────────────────────────────────────────

export interface Coord {
  page: number
  x: number
  y: number
  str: string
  /** Text width in PDF units — needed to classify right-aligned numbers by their right edge. */
  w: number
}

// ── Metadata extraction ───────────────────────────────────────────────────

interface StatementMetadata {
  accountType: "savings" | "current"
  accountLast4: string | null
  periodStart: string | null
  periodEnd: string | null
}

function extractMetadata(items: Coord[]): StatementMetadata {
  // Collect all text from the header zone of every page, grouped into row strings.
  // The per-page header block lives at y ≥ 619; the "Statement From" row is at y ≈ 620.
  const headerItems = items.filter((i) => i.y > Y_DATA_MAX)
  const rowStrings = groupIntoRowStrings(headerItems)
  const fullText = rowStrings.join(" ")

  let accountType: "savings" | "current" = "savings"
  let accountLast4: string | null = null
  let periodStart: string | null = null
  let periodEnd: string | null = null

  // "Account No : 50100565912523   PRIME POTENTIAL"
  const accountMatch = fullText.match(/Account No\s*:?\s*(\d{10,18})/)
  if (accountMatch) {
    accountLast4 = accountMatch[1].slice(-4)
  }

  // "Account Type : SAVINGS - RESIDENTS (113)" or "CURRENT"
  if (/current/i.test(fullText) && /account.*type/i.test(fullText)) {
    accountType = "current"
  }

  // FY statement:    "Statement From : 01/04/2025   To : 31/03/2026"
  // Monthly statement: "From : 01/01/2026   To : 31/01/2026"
  const periodMatch = fullText.match(
    /(?:Statement\s+)?From\s*:\s*(\d{2}\/\d{2}\/\d{4})\s+To\s*:\s*(\d{2}\/\d{2}\/\d{4})/i,
  )
  if (periodMatch) {
    periodStart = parseHdfcDate(periodMatch[1])
    periodEnd = parseHdfcDate(periodMatch[2])
  }

  return { accountType, accountLast4, periodStart, periodEnd }
}

/** Group text items by (page, y) into single row strings (sorted left-to-right). */
function groupIntoRowStrings(items: Coord[]): string[] {
  const buckets = new Map<string, Coord[]>()

  for (const item of items) {
    const pagePrefix = `${item.page}:`
    let bucketKey: string | null = null
    for (const key of buckets.keys()) {
      if (!key.startsWith(pagePrefix)) continue
      if (Math.abs(item.y - parseInt(key.slice(pagePrefix.length), 10)) <= ROW_TOLERANCE) {
        bucketKey = key
        break
      }
    }
    if (bucketKey === null) {
      bucketKey = `${item.page}:${item.y}`
      buckets.set(bucketKey, [item])
    } else {
      const existing = buckets.get(bucketKey)
      if (existing) existing.push(item)
    }
  }

  return [...buckets.values()].map((row) =>
    row
      .sort((a, b) => a.x - b.x)
      .map((c) => c.str)
      .join(" "),
  )
}

// ── Transaction parsing ───────────────────────────────────────────────────

/** A pending transaction being assembled from PDF rows. */
interface PendingRow {
  dateStr: string
  narrationParts: string[]
  refNo: string | null
  valueDateStr: string | null
  withdrawalStr: string | null
  depositStr: string | null
  balanceStr: string | null
}

// ── Layouts ──────────────────────────────────────────────────────────────
// HDFC has shipped two savings-statement table layouts:
//   classic: Date | Narration | Chq./Ref.No. | Value Dt | Withdrawal Amt. | Deposit Amt. | Closing Balance
//   compact: Txn Date | Narration | Withdrawals | Deposits | Closing Balance  (no ref / value-date
//            columns; "Value Dt dd/mm/yyyy" is appended to the narration)
// Reading a compact statement with the classic x-boundaries shifts every amount one column
// left/right (the closing balance was read as the deposit — every row became a huge credit).
type Layout = "classic" | "compact"

// Compact layout: amounts are right-aligned, so classify by right edge (x + width), not x.
// Right edges observed on a real statement: withdrawals ≈ 358, deposits ≈ 444, balance ≈ 561.
const COMPACT_DATE_MAX_X = 90
const COMPACT_NARRATION_MIN_X = 90
const COMPACT_NUMERIC_MIN_X = 300
const COMPACT_WITHDRAWAL_MAX_RIGHT = 400
const COMPACT_DEPOSIT_MAX_RIGHT = 500
const AMOUNT_RE = /^[\d,]+\.\d{2}$/

function detectLayout(items: Coord[]): Layout {
  const hdr = items.find((i) => /^Narration$/i.test(i.str.trim()))
  if (!hdr) return "classic"
  const headerRow = items.filter(
    (i) => i.page === hdr.page && Math.abs(i.y - hdr.y) <= ROW_TOLERANCE,
  )
  // Only the classic layout has Chq./Ref.No. and Value Dt columns.
  const isClassic = headerRow.some((i) => /chq|ref\.?\s*no|value\s*dt/i.test(i.str))
  return isClassic ? "classic" : "compact"
}

interface RowCells {
  dateCell?: Coord
  narrationCells: Coord[]
  refCell?: Coord
  valueDtCell?: Coord
  withdrawalCell?: Coord
  depositCell?: Coord
  balanceCell?: Coord
}

function classifyRow(row: Coord[], layout: Layout): RowCells {
  if (layout === "compact") {
    const amounts = row.filter((c) => c.x >= COMPACT_NUMERIC_MIN_X && AMOUNT_RE.test(c.str.trim()))
    const right = (c: Coord) => c.x + c.w
    return {
      dateCell: row.find((c) => c.x <= COMPACT_DATE_MAX_X),
      narrationCells: row.filter(
        (c) => c.x > COMPACT_NARRATION_MIN_X && c.x < COMPACT_NUMERIC_MIN_X,
      ),
      withdrawalCell: amounts.find((c) => right(c) < COMPACT_WITHDRAWAL_MAX_RIGHT),
      depositCell: amounts.find(
        (c) => right(c) >= COMPACT_WITHDRAWAL_MAX_RIGHT && right(c) < COMPACT_DEPOSIT_MAX_RIGHT,
      ),
      balanceCell: amounts.find((c) => right(c) >= COMPACT_DEPOSIT_MAX_RIGHT),
    }
  }
  return {
    dateCell: row.find((c) => c.x <= X_DATE_MAX),
    narrationCells: row.filter((c) => c.x >= X_NARRATION_MIN && c.x < X_NARRATION_MAX),
    refCell: row.find((c) => c.x >= X_REF_MIN && c.x < X_VALUEDT_MIN),
    valueDtCell: row.find((c) => c.x >= X_VALUEDT_MIN && c.x < X_WITHDRAWAL_MIN),
    withdrawalCell: row.find((c) => c.x >= X_WITHDRAWAL_MIN && c.x < X_WITHDRAWAL_MAX),
    depositCell: row.find((c) => c.x >= X_DEPOSIT_MIN && c.x < X_DEPOSIT_MAX),
    balanceCell: row.find((c) => c.x >= X_BALANCE_MIN),
  }
}

/**
 * Parse transactions from pre-extracted PDF text items (exported for tests).
 * Throws if the running balance doesn't reconcile — that means the columns were
 * misread, and returning the rows would silently corrupt imported data.
 */
export function parseHdfcSavingsItems(items: Coord[]): ParsedTransaction[] {
  const transactions = parseTransactions(items, detectLayout(items))
  assertBalanceContinuity(transactions)
  return transactions
}

/** Each row's closing balance must equal previous balance ± its amount. */
function assertBalanceContinuity(txns: ParsedTransaction[]): void {
  let checked = 0
  let bad = 0
  for (let i = 1; i < txns.length; i++) {
    const prev = txns[i - 1].closingBalance
    const cur = txns[i].closingBalance
    if (prev == null || cur == null) continue
    checked++
    const expected = prev + (txns[i].type === "credit" ? txns[i].amount : -txns[i].amount)
    if (Math.abs(expected - cur) > 0.02) bad++
  }
  if (checked >= 5 && bad / checked > 0.25) {
    throw new Error(
      `HDFC savings PDF: column layout not recognised — ${bad}/${checked} rows fail the running-balance check. Refusing to return misread amounts.`,
    )
  }
}

function parseTransactions(items: Coord[], layout: Layout): ParsedTransaction[] {
  // Work only with data-zone items
  const dataItems = items.filter((i) => i.y > Y_DATA_MIN && i.y <= Y_DATA_MAX)

  // Group into rows (sorted y descending = top to bottom on page)
  const rows = buildRows(dataItems)

  const transactions: ParsedTransaction[] = []
  let pending: PendingRow | null = null

  for (const row of rows) {
    const {
      dateCell,
      narrationCells,
      refCell,
      valueDtCell,
      withdrawalCell,
      depositCell,
      balanceCell,
    } = classifyRow(row, layout)

    const dateStr = dateCell?.str.trim() ?? ""
    const isDateRow = /^\d{2}\/\d{2}\/\d{2,4}$/.test(dateStr)
    const isColumnHeader = narrationCells.some((c) => c.str === "Narration" || c.str === "Date")

    if (isColumnHeader) continue // skip the header row on page 1

    if (isDateRow) {
      // Flush the previous pending transaction
      if (pending) {
        const txn = buildTransaction(pending)
        if (txn) transactions.push(txn)
      }

      pending = {
        dateStr,
        narrationParts: narrationCells.map((c) => c.str),
        refNo: refCell?.str.trim() ?? null,
        valueDateStr: valueDtCell?.str.trim() ?? null,
        withdrawalStr: withdrawalCell?.str.trim() ?? null,
        depositStr: depositCell?.str.trim() ?? null,
        balanceStr: balanceCell?.str.trim() ?? null,
      }
    } else if (narrationCells.length > 0 && !refCell && !withdrawalCell && !depositCell) {
      // Continuation row — only narration items, no date/ref/amount.
      // "STATEMENT SUMMARY" marks the end of transaction data — stop processing.
      const continuationText = narrationCells.map((c) => c.str).join(" ")
      if (/STATEMENT SUMMARY/i.test(continuationText)) break
      if (pending) {
        pending.narrationParts.push(...narrationCells.map((c) => c.str))
      }
    }
    // Otherwise: row is unrecognised metadata — skip
  }

  // Flush the last pending transaction
  if (pending) {
    const txn = buildTransaction(pending)
    if (txn) transactions.push(txn)
  }

  return transactions
}

/**
 * Group text items into rows.
 * Key insight: y-coordinates are PAGE-relative, so two items with the same y
 * on different pages must not be merged. Uses "page-y" composite bucket keys.
 * Rows are sorted document-order: page ascending, then y descending (top→bottom).
 */
function buildRows(items: Coord[]): Coord[][] {
  // bucketKey: "page:roundedY"
  const buckets = new Map<string, Coord[]>()

  for (const item of items) {
    const pagePrefix = `${item.page}:`
    let bucketKey: string | null = null

    for (const key of buckets.keys()) {
      if (!key.startsWith(pagePrefix)) continue
      const bucketY = parseInt(key.slice(pagePrefix.length), 10)
      if (Math.abs(item.y - bucketY) <= ROW_TOLERANCE) {
        bucketKey = key
        break
      }
    }

    if (bucketKey === null) {
      bucketKey = `${item.page}:${item.y}`
      buckets.set(bucketKey, [item])
    } else {
      const existing = buckets.get(bucketKey)
      if (existing) existing.push(item)
    }
  }

  return [...buckets.entries()]
    .sort((a, b) => {
      const [pa, ya] = a[0].split(":").map(Number)
      const [pb, yb] = b[0].split(":").map(Number)
      return pa !== pb ? pa - pb : yb - ya // page asc, y desc (top→bottom)
    })
    .map(([, row]) => row.sort((r1, r2) => r1.x - r2.x))
}

function buildTransaction(row: PendingRow): ParsedTransaction | null {
  const date = parseHdfcDate(row.dateStr)
  if (!date) return null

  // Join narration parts with a space — PDF wraps the text into multiple items/lines.
  let narration = row.narrationParts
    .join(" ")
    .replace(/\s{2,}/g, " ")
    .trim()
  // Compact layout appends "Value Dt dd/mm/yyyy" to the narration (classic has a column).
  // Lift it into valueDate and drop it from the description so descriptions stay
  // comparable across layouts.
  let embeddedValueDate: string | null = null
  const vd = narration.match(/\s*Value\s*Dt\s*(\d{2}\/\d{2}\/\d{2,4})/i)
  if (vd) {
    embeddedValueDate = parseHdfcDate(vd[1])
    narration = narration
      .replace(vd[0], "")
      .replace(/\s{2,}/g, " ")
      .trim()
  }
  if (!narration) return null

  const withdrawal = parseAmount(row.withdrawalStr)
  const deposit = parseAmount(row.depositStr)
  if (withdrawal === 0 && deposit === 0) return null

  const amount = withdrawal > 0 ? withdrawal : deposit
  const type: "debit" | "credit" = withdrawal > 0 ? "debit" : "credit"

  const valueDate = row.valueDateStr ? parseHdfcDate(row.valueDateStr) : embeddedValueDate
  const closingBalance = parseAmount(row.balanceStr) || null

  const parsed = parseHdfcNarration(narration)

  return {
    date,
    description: narration,
    amount,
    type,
    referenceNumber: row.refNo || null,
    valueDate,
    closingBalance,
    paymentMethod: parsed.paymentMethod,
    extractedName: parsed.name,
    upiId: parsed.upiId,
    sourceCategory: null,
  }
}

// ── Utilities ────────────────────────────────────────────────────────────

/**
 * Parse HDFC date formats:
 *   "DD/MM/YY"   → "YYYY-MM-DD"
 *   "DD/MM/YYYY" → "YYYY-MM-DD"
 */
function parseHdfcDate(raw: string): string | null {
  const match = raw.trim().match(/^(\d{2})\/(\d{2})\/(\d{2,4})$/)
  if (!match) return null

  const day = match[1]
  const month = match[2]
  let year = match[3]

  if (year.length === 2) {
    const num = parseInt(year, 10)
    year = num > 50 ? `19${year}` : `20${year}`
  }

  return `${year}-${month}-${day}`
}

function parseAmount(raw: string | null | undefined): number {
  if (!raw) return 0
  const cleaned = raw.replace(/,/g, "").trim()
  const num = parseFloat(cleaned)
  return Number.isNaN(num) ? 0 : Math.abs(num)
}
